import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Logo } from "./Logo";
import { useAuth } from "@/context/AuthContext";
import { Search, Heart, Menu, X, User, LogOut, LayoutDashboard, CalendarDays, Flag, Receipt } from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

const NAV = [
  { label: "Find Races", to: "/races" },
  { label: "Calendar", to: "/calendar" },
  { label: "For Organizers", to: "/for-organizers" },
  { label: "About", to: "/about" },
];

export function Header() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [showSearch, setShowSearch] = useState(false);

  const doSearch = (e) => {
    e.preventDefault();
    nav(`/races?search=${encodeURIComponent(q)}`);
    setShowSearch(false);
  };

  const isActive = (to) => loc.pathname === to || (to !== "/" && loc.pathname.startsWith(to));

  const dashLink = user?.role === "admin" ? "/admin" : user?.role === "organizer" ? "/organizer" : "/dashboard";

  return (
    <>
    <header className="sticky top-0 z-50 bg-[#0a1420]/95 backdrop-blur-md border-b border-slate-800" data-testid="site-header">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <Logo />
          <nav className="hidden lg:flex items-center gap-8">
            {NAV.map((n) => (
              <Link key={n.to} to={n.to} data-testid={`nav-${n.to.slice(1) || "home"}`}
                className={`font-body text-sm font-semibold tracking-wide transition-colors pb-1 border-b-2 ${
                  isActive(n.to) ? "text-white border-[#22d3ee]" : "text-slate-300 border-transparent hover:text-white"
                }`}>
                {n.label}
              </Link>
            ))}
          </nav>
          <div className="hidden lg:flex items-center gap-3">
            <button onClick={() => setShowSearch((s) => !s)} className="p-2 text-slate-300 hover:text-white" data-testid="header-search-toggle">
              <Search size={20} />
            </button>
            {user ? (
              <>
                <Link to="/my-races" className="p-2 text-slate-300 hover:text-[#22d3ee]" data-testid="header-favourites">
                  <Heart size={20} />
                </Link>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button className="flex items-center gap-2 text-white" data-testid="header-user-menu">
                      {user.picture ? (
                        <img src={user.picture} alt="" className="w-8 h-8 rounded-full object-cover border border-slate-600" />
                      ) : (
                        <span className="w-8 h-8 rounded-full bg-[#22d3ee] text-[#0a1420] grid place-items-center font-bold">
                          {user.name?.[0]?.toUpperCase()}
                        </span>
                      )}
                      <span className="text-sm font-semibold">Hi, {user.name?.split(" ")[0]}</span>
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-52">
                    <DropdownMenuItem onClick={() => nav(dashLink)} data-testid="menu-dashboard"><LayoutDashboard size={16} className="mr-2" />Dashboard</DropdownMenuItem>
                    <DropdownMenuItem onClick={() => nav("/my-races")}><Heart size={16} className="mr-2" />My Races</DropdownMenuItem>
                    <DropdownMenuItem onClick={() => nav("/calendar")}><CalendarDays size={16} className="mr-2" />Calendar</DropdownMenuItem>
                    {user?.role === "organizer" && <DropdownMenuItem onClick={() => nav("/organizer/billing")} data-testid="menu-invoices"><Receipt size={16} className="mr-2" />Invoices</DropdownMenuItem>}
                    <DropdownMenuItem onClick={() => nav("/profile")}><User size={16} className="mr-2" />Profile</DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={async () => { await logout(); nav("/"); }} data-testid="menu-logout"><LogOut size={16} className="mr-2" />Log Out</DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            ) : (
              <>
                <Link to="/login" className="font-body text-sm font-semibold text-white px-4 py-2 hover:text-[#22d3ee]" data-testid="header-login">Log In</Link>
                <Link to="/register" className="font-body text-sm font-bold bg-[#22d3ee] text-[#0a1420] px-5 py-2 rounded-lg hover:bg-[#06b6d4] transition-colors" data-testid="header-signup">Sign Up</Link>
              </>
            )}
          </div>
          <button className="lg:hidden p-2 text-white" onClick={() => setOpen(true)} data-testid="mobile-menu-open" aria-label="Open menu">
            <Menu size={24} />
          </button>
        </div>
        {showSearch && (
          <form onSubmit={doSearch} className="hidden lg:block pb-4">
            <div className="relative">
              <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search races, cities, sports..."
                className="w-full bg-navy-card text-white border border-slate-700 rounded-lg pl-10 pr-4 py-2.5 focus:border-[#22d3ee] outline-none" data-testid="header-search-input" />
            </div>
          </form>
        )}
      </div>

    </header>

      {/* Mobile drawer — sibling of <header> so fixed positioning is relative to the viewport, not the backdrop-blurred header */}
      {open && (
        <div className="lg:hidden fixed inset-0 z-[100] bg-[#0a1420] text-white flex flex-col" data-testid="mobile-menu">
          <div className="flex items-center justify-between h-16 px-4 border-b border-slate-800">
            <Logo />
            <button onClick={() => setOpen(false)} className="p-2" data-testid="mobile-menu-close"><X size={24} /></button>
          </div>
          <nav className="flex-1 overflow-y-auto p-4 space-y-1">
            {[{ label: "Find Races", to: "/races", icon: Flag },
              { label: "Calendar", to: "/calendar", icon: CalendarDays },
              { label: "For Organizers", to: "/for-organizers", icon: LayoutDashboard },
              { label: "About", to: "/about", icon: User }]
              .map((n) => {
                const Icon = n.icon;
                return (
                  <Link key={n.to} to={n.to} onClick={() => setOpen(false)}
                    className="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-200 hover:bg-navy-card font-semibold" data-testid={`mobile-nav-${n.to.slice(1) || "home"}`}>
                    <Icon size={20} />{n.label}
                  </Link>
                );
              })}
          </nav>
          <div className="p-4 border-t border-slate-800 space-y-2">
            {user ? (
              <>
                <Link to={dashLink} onClick={() => setOpen(false)} className="block w-full text-center bg-navy-card py-3 rounded-lg font-semibold">Dashboard</Link>
                <button onClick={async () => { await logout(); setOpen(false); nav("/"); }} className="block w-full text-center border border-slate-700 py-3 rounded-lg font-semibold">Log Out</button>
              </>
            ) : (
              <>
                <Link to="/login" onClick={() => setOpen(false)} className="block w-full text-center border border-slate-600 py-3 rounded-lg font-semibold">Log In</Link>
                <Link to="/register" onClick={() => setOpen(false)} className="block w-full text-center bg-[#22d3ee] text-[#0a1420] py-3 rounded-lg font-bold">Sign Up</Link>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}
